import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, ArrowRight, Phone, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";
import { CONTACT, telLink, waLink } from "../lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadDialog({ open, onClose, plan }) {
  const { t, lang } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", business_type: "", message: "" });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, plan: plan || null, language: lang, source: "website" });
      toast.success(t.form.success);
      setForm({ name: "", phone: "", business_type: "", message: "" });
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error(t.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="lead-dialog"
        >
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="relative w-full md:max-w-md bg-white rounded-t-3xl md:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden max-h-[92vh] overflow-y-auto"
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
          >
            <div className="absolute inset-x-0 top-0 h-1.5 ring-gradient" />
            <button
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center z-10"
              onClick={onClose}
              data-testid="lead-dialog-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-6 md:p-8">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">
                <Sparkles className="w-3.5 h-3.5" /> {plan ? `${plan} Plan` : "Free Consultation"}
              </div>
              <h3 className="font-display text-2xl md:text-[28px] font-bold mt-2.5 text-slate-950 leading-tight">
                Talk to us — we pick up fast.
              </h3>
              <p className="text-slate-600 mt-2 text-sm">
                The fastest way is a quick call. We'll understand your business in 5 minutes.
              </p>

              {/* PRIORITY: Call Now */}
              <a
                href={telLink()}
                data-testid="lead-call-now"
                className="mt-5 group relative w-full flex items-center justify-between gap-3 h-16 px-5 rounded-2xl bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition active:scale-[0.99]"
              >
                <span className="absolute inset-0 rounded-2xl ring-2 ring-blue-400/40 animate-pulse-ring-blue pointer-events-none" />
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-100">
                      Call now — instant
                    </div>
                    <div className="font-display text-[17px] font-bold leading-tight">
                      {CONTACT.phoneDisplay}
                    </div>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
              </a>

              {/* Secondary: WhatsApp */}
              <a
                href={waLink(plan ? `Hi Launch Mitra, I'm interested in the ${plan} plan.` : undefined)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-whatsapp"
                className="mt-3 flex items-center justify-between h-13 px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 text-emerald-900 font-semibold transition"
              >
                <span className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                    <MessageCircle className="w-4 h-4" />
                  </span>
                  <span className="text-sm">Chat on WhatsApp</span>
                </span>
                <ArrowRight className="w-4 h-4 text-emerald-700" />
              </a>

              {/* Divider */}
              <div className="my-5 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                <span className="flex-1 h-px bg-slate-200" />
                or, leave details — we'll call you
                <span className="flex-1 h-px bg-slate-200" />
              </div>

              <form className="space-y-2.5" onSubmit={submit}>
                <Input
                  data-testid="lead-name-input"
                  placeholder={t.form.name}
                  value={form.name}
                  onChange={onChange("name")}
                  required
                  className="h-11 rounded-xl bg-slate-50 border-slate-200"
                />
                <Input
                  data-testid="lead-phone-input"
                  placeholder={t.form.phone}
                  value={form.phone}
                  onChange={onChange("phone")}
                  required
                  inputMode="tel"
                  className="h-11 rounded-xl bg-slate-50 border-slate-200"
                />
                <Input
                  data-testid="lead-business-input"
                  placeholder={t.form.typePh}
                  value={form.business_type}
                  onChange={onChange("business_type")}
                  className="h-11 rounded-xl bg-slate-50 border-slate-200"
                />
                <Textarea
                  data-testid="lead-message-input"
                  placeholder={t.form.message}
                  value={form.message}
                  onChange={onChange("message")}
                  rows={2}
                  className="rounded-xl bg-slate-50 border-slate-200 resize-none"
                />

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-submit-btn"
                  className="w-full h-11 rounded-full text-sm font-semibold bg-slate-950 hover:bg-slate-800 text-white"
                >
                  {submitting ? t.form.submitting : t.form.submit}
                  {!submitting && <ArrowRight className="w-4 h-4 ml-1" />}
                </Button>
                <p className="text-[10px] text-slate-500 text-center pt-1">
                  By submitting, you agree to be contacted on WhatsApp/phone. No spam.
                </p>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
